import React, { type ComponentPropsWithoutRef } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { SpotlightManager, SpotlightTarget, SpotlightTransition } from '@atlaskit/onboarding';
import {
	ContextualAnalyticsData,
	FireScreenAnalytics,
	MODAL,
} from '@atlassian/jira-product-analytics-bridge';
import { useCompassComponentsSpotlightViewed } from '../../common/services/use-compass-components-spotlight-viewed/use-compass-components-spotlight-viewed.tsx';
import { CompassComponentsPopup } from './compass-components-popup/compass-components-popup.tsx';

type CompassComponentsPopupProps = ComponentPropsWithoutRef<typeof CompassComponentsPopup>;
type BaseProps = Omit<CompassComponentsPopupProps, 'target'>;

export interface CompassComponentsSpotlightProps extends BaseProps {
	isVisible?: boolean;
	children: React.ReactElement;
}

const TARGET = 'project-directories.components.compass-components-spotlight.target';

export function CompassComponentsSpotlight({
	isVisible = true,
	onDismiss,
	onLearnMore,
	children,
	...forwardProps
}: CompassComponentsSpotlightProps) {
	const [{ hasBeenViewed }, { hide }] = useCompassComponentsSpotlightViewed();

	const handleDismiss = (e: React.MouseEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => {
		hide();

		onDismiss?.(e, analyticsEvent);
	};

	const handleLearnMore = (e: React.MouseEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => {
		hide();

		onLearnMore?.(e, analyticsEvent);
	};

	return (
		<SpotlightManager blanketIsTinted={false}>
			<SpotlightTarget name={TARGET}>{children}</SpotlightTarget>

			<SpotlightTransition>
				{isVisible && !hasBeenViewed && (
					<ContextualAnalyticsData sourceType={MODAL} sourceName="compassComponentsSpotlight">
						<FireScreenAnalytics />
						<CompassComponentsPopup
							target={TARGET}
							onDismiss={handleDismiss}
							onLearnMore={handleLearnMore}
							{...forwardProps}
						/>
					</ContextualAnalyticsData>
				)}
			</SpotlightTransition>
		</SpotlightManager>
	);
}
